import React, { useEffect, useRef, useState } from "react";
import { X, Heart } from "lucide-react";
import { usePlayer } from "@/lib/player";

/**
 * TipPrompt — track-based, well-timed call-to-action.
 *
 * NEW behaviour (per user feedback): the time-based 5-minute trigger was missing because
 * many listeners on mobile pause / switch tabs (the wall-clock kept running even when
 * audio wasn't actually playing). Now it counts ACTUAL songs the listener heard.
 *
 *   1st prompt:  immediately after the listener finishes their first song on a station.
 *   Recurring:   every 3 songs after that (i.e. song #4, #7, #10, …).
 *
 * Cooldowns:
 *   - Dismissed → 12 hour cooldown on that station before the next prompt may show.
 *   - Tipped    → 7 day cooldown (give them grace).
 *
 * Counter resets when the listener changes stations.
 */

const COOLDOWN_AFTER_DISMISS_MS = 12 * 60 * 60 * 1000;
const COOLDOWN_AFTER_TIP_MS = 7 * 24 * 60 * 60 * 1000;

function buildTipUrl(base, amount) {
  if (!base || !amount) return base;
  try {
    if (/paypal\.me\//i.test(base)) return base.replace(/\/+$/, "") + `/${amount}`;
    if (/venmo\.com\//i.test(base)) {
      const sep = base.includes("?") ? "&" : "?";
      return `${base}${sep}amount=${amount}`;
    }
    if (/cash\.app\//i.test(base) || /cash\.me\//i.test(base)) {
      return base.replace(/\/+$/, "") + `/${amount}`;
    }
    if (/buy\.stripe\.com/i.test(base)) {
      const sep = base.includes("?") ? "&" : "?";
      return `${base}${sep}prefilled_amount=${amount}00`;
    }
  } catch (_) {}
  return base;
}

export default function TipPrompt() {
  const { station, current } = usePlayer();
  const [open, setOpen] = useState(false);
  const [songsHeard, setSongsHeard] = useState(0);

  const stationIdRef = useRef(null);
  const lastTrackIdRef = useRef(null);
  const countRef = useRef(0);

  // Reset when listener tunes into a different station
  useEffect(() => {
    if (!station) {
      stationIdRef.current = null;
      lastTrackIdRef.current = null;
      countRef.current = 0;
      setSongsHeard(0);
      setOpen(false);
      return;
    }
    if (stationIdRef.current !== station.station_id) {
      stationIdRef.current = station.station_id;
      lastTrackIdRef.current = current?.track_id || null;
      countRef.current = 0;
      setSongsHeard(0);
      setOpen(false);
    }
  }, [station, current]);

  // Detect track transitions → increment song count → trigger prompt
  useEffect(() => {
    if (!station || !station.tip_jar_url || !current) return;
    if (!lastTrackIdRef.current) {
      lastTrackIdRef.current = current.track_id;
      return;
    }
    if (current.track_id !== lastTrackIdRef.current) {
      // A song finished and the next started — that's +1 song heard.
      lastTrackIdRef.current = current.track_id;
      const next = countRef.current + 1;
      countRef.current = next;
      setSongsHeard(next);

      // Prompt rule: trigger on song #1 finished, then every 3rd (4, 7, 10, …)
      const shouldPrompt = next === 1 || (next > 1 && (next - 1) % 3 === 0);
      if (!shouldPrompt) return;

      // Respect cooldown
      try {
        const raw = localStorage.getItem(`fliboe_tip_state_${station.station_id}`);
        if (raw) {
          const { until } = JSON.parse(raw);
          if (until && Date.now() < until) return;
        }
      } catch (_) {}
      setOpen(true);
    }
  }, [current, station]);

  const persist = (ms) => {
    if (!station) return;
    try {
      localStorage.setItem(
        `fliboe_tip_state_${station.station_id}`,
        JSON.stringify({ until: Date.now() + ms })
      );
    } catch (_) {}
  };

  const dismiss = () => { persist(COOLDOWN_AFTER_DISMISS_MS); setOpen(false); };
  const tip = (amount) => {
    const url = buildTipUrl(station.tip_jar_url, amount);
    window.open(url, "_blank", "noopener,noreferrer");
    persist(COOLDOWN_AFTER_TIP_MS);
    setOpen(false);
  };

  if (!open || !station?.tip_jar_url) return null;

  const songsLine = songsHeard <= 1
    ? "You just heard your first track on this station."
    : `You've enjoyed ${songsHeard} tracks on this station so far.`;

  return (
    <div
      className="fixed bottom-28 right-6 z-[55] w-[360px] max-w-[calc(100vw-2rem)] fl-card !p-0 overflow-hidden shadow-2xl shadow-black/60"
      data-testid="tip-prompt"
      style={{ animation: "tip-rise 360ms cubic-bezier(.2,.9,.3,1)" }}
    >
      <style>{`@keyframes tip-rise { from { transform: translateY(24px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>

      <div className="relative px-5 pt-5 pb-3">
        <button onClick={dismiss} className="absolute top-3 right-3 text-slate-500 hover:text-white" aria-label="Close" data-testid="tip-prompt-close"><X className="w-4 h-4" /></button>

        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center shrink-0">
            <Heart className="w-5 h-5 text-emerald-500" />
          </div>
          <div className="min-w-0">
            <div className="fl-mono text-[10px] tracking-[0.25em] uppercase text-emerald-500">// Loving the vibe?</div>
            <div className="fl-display text-lg font-bold leading-tight mt-0.5">{station.name}</div>
          </div>
        </div>

        <p className="mt-3 text-sm text-slate-300 leading-relaxed">
          {songsLine} It's free, ad-free, worldwide — and the host pays to keep this station on the air.{" "}
          <span className="text-white">A tip says thanks and keeps the music coming.</span>
        </p>
        <p className="mt-2 text-[11px] text-slate-500 fl-mono tracking-wide">
          100% goes to the host. Fliboe takes nothing.
        </p>
      </div>

      <div className="px-5 pb-4 pt-1 grid grid-cols-3 gap-2">
        {[2, 5, 10].map((amt) => (
          <button key={amt} onClick={() => tip(amt)} className="fl-btn !py-2.5 !px-0 text-sm" data-testid={`tip-amount-${amt}`}>
            ${amt}
          </button>
        ))}
      </div>

      <button onClick={() => tip("")} className="block w-full text-center px-5 pb-4 text-xs text-slate-400 hover:text-emerald-500" data-testid="tip-custom">
        Tip a custom amount →
      </button>

      <button onClick={dismiss} className="block w-full text-center px-5 pb-4 -mt-2 text-[11px] text-slate-600 hover:text-slate-400 fl-mono uppercase tracking-widest" data-testid="tip-prompt-later">
        Maybe next song
      </button>
    </div>
  );
}
